import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getBusDemands } from '../../../../api/endpoints/demands';
import type { BusDemand } from '../../../../api/types/demands';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { BusDemandDialog } from '../BusDemandDialog';

/**
 * List rendered as a plain MUI Table rather than @mui/x-data-grid: that package's v9
 * typings (via @mui/x-internals) use TS 5.0-only `const` type-parameter syntax, which is a
 * hard parse error under this project's pinned TypeScript 4.9.5 — confirmed by isolating the
 * import (no other Phase 1 module uses DataGrid either). A Table keeps `tsc --noEmit` clean.
 */
export default function BusDemandPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<BusDemand | null>(null);

  const query = useQuery({
    queryKey: ['demands', 'bus'],
    queryFn: () => getBusDemands(),
  });

  const rows: BusDemand[] = query.data?.busDemands ?? [];

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Otobüs Bileti Talepleri</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Talep
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Yolcu</TableCell>
              <TableCell>Kalkış</TableCell>
              <TableCell>Varış</TableCell>
              <TableCell>Gidiş Tarihi</TableCell>
              <TableCell>Dönüş Tarihi</TableCell>
              <TableCell>Personel ID</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı talep bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{[row.passengerName, row.passengerSurname].filter(Boolean).join(' ') || '—'}</TableCell>
                <TableCell>{row.from ?? '—'}</TableCell>
                <TableCell>{row.to ?? '—'}</TableCell>
                <TableCell>{row.depDate ?? '—'}</TableCell>
                <TableCell>{row.retDate ?? '—'}</TableCell>
                <TableCell>{row.employeeID ?? '—'}</TableCell>
                <TableCell>
                  <Chip size="small" color={row.isTicketed ? 'success' : 'default'} label={row.isTicketed ? 'Biletlendi' : 'Beklemede'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <BusDemandDialog open={createOpen} demand={null} onClose={() => setCreateOpen(false)} />
      <BusDemandDialog open={!!editing} demand={editing} onClose={() => setEditing(null)} />
    </Box>
  );
}
